import { publicApi } from "@api";
import GetCodeButton from "@components/inputs/GetCodeButton";
import { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import PasswordTextField from "@components/inputs/PasswordTextField";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { ForgotSecretCallbackProps } from "@components/utils/Auth/types/auth-props";
import { envConfig } from "@configs/env";
import { OTPMethod, OTPSource, useStatsigTracking } from "@configs/statsig";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  DEFAULT_OTP_COOLDOWN_SECONDS,
  getOtpCooldownSecondsFromRateLimitError,
  getOtpRemainingSecondsFromNextRequestAt,
} from "@utils/otp-cooldown";
import {
  APIErrorCodes,
  Button,
  PropsWithClassName,
  TextField,
  cn,
  delay,
  standardizePhoneNumber,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPhoneAndroid } from "react-icons/md";
import { z } from "zod";

interface ForgotSecretUPProps extends ForgotSecretCallbackProps, PropsWithClassName {
  defaultPhone?: string;
  phoneReadonly?: boolean;
  deferUpdateToken?: boolean;
}

const inputClasses = {
  "input&": "w-full !px-0 !ps-11 text-start",
  "iconContainer&": "!start-3 !top-[10px]",
};

const ForgotSecretUP = ({
  defaultPhone,
  phoneReadonly,
  onLoginSuccess,
  onForgotSecretSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  className,
  deferUpdateToken = false,
}: ForgotSecretUPProps) => {
  const { t, i18n } = useTranslation();
  const { phonePrefix, phoneMinLength, phoneMaxLength, phoneStartWith, showPhoneInputPrefix, phoneSimpleError } =
    useMemberSettings();
  const [sendOTP, { isLoading: isSendingOTP }] = publicApi.useCreateForgotPasswordMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const [submitForgotPwd, { isLoading: isSubmittingForgotPwd }] = publicApi.useSubmitResetPasswordMutation();
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess,
    onPostLoginCheckPassed,
    onPostLoginCheckFailed,
  });
  const { trackOTPSent, trackOTPRequestFailed, trackOTPVerified, trackOTPFailedAttempt, trackOTPRequest } =
    useStatsigTracking();

  const forgotPasswordFormSchema = useMemo(() => {
    return z.object({
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "digits",
      }),
      sms: ValidatorSchema.otp,
      password: ValidatorSchema.registerPassword,
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  const form = useForm<z.infer<typeof forgotPasswordFormSchema>>({
    mode: "onChange",
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      phone: defaultPhone,
    },
  });

  const {
    handleSubmit: handleFormSubmit,
    register: formRegister,
    watch,
    formState: { errors: formErrors, submitCount },
    trigger,
    getValues,
    setError,
    setFocus,
  } = form;

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: postLoginCheck,
    deferUpdateToken,
  });

  // Capture form validation errors
  useCaptureFormValidationErrors(form, { feature: "forgot-password" });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount,
    trigger: trigger,
    errors: formErrors,
  });

  const onSubmitForm = async (data: z.infer<typeof forgotPasswordFormSchema>) => {
    const { phone, sms, password } = data;
    try {
      await handleVerifyOTP(sms, phone);

      const resSubmitForgotPwd = await submitForgotPwd({
        accountId: envConfig.accountId,
        phone: standardizePhoneNumber(phone, phonePrefix),
        code: sms,
        password,
      });

      handleApiResponse(resSubmitForgotPwd, {
        successMessage: t("password updated successfully"),
        toastSuccess: true,
        toastError: false,
        onSuccess: (response) => {
          const clientId = response?.accessKey;
          if (clientId) {
            handleLogin(clientId, password);
          }
          onForgotSecretSuccess?.();
        },
        onError: (error) => {
          if (error?.data?.error?.message === APIErrorCodes.Member.PhoneNotRegistered) {
            setError("phone", { message: t("number not registered. please try another") });
            return;
          }
          toast.error(resolveAuthResponseError(error, t));
        },
      });
    } catch (error) {
      setError("sms", { message: error as string });
    }
  };

  const handleGetCode = async () => {
    setFocus("sms");
    return new Promise((resolve, reject) => {
      trigger(["phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }

        const { phone } = getValues();
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.FORGOT_PASSWORD });
        sendOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastSuccess: false,
            toastError: false,
            onSuccess: (otpRes) => {
              toast.success(getOTPCodeSentMessage(otpRes.method, i18n.language, t), { duration: 8000 });
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = nextRequestAt
                ? getOtpRemainingSecondsFromNextRequestAt(nextRequestAt)
                : DEFAULT_OTP_COOLDOWN_SECONDS;
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              // Track OTP sent with forgot source
              trackOTPSent(otpRes.method as OTPMethod, { source: OTPSource.FORGOT_PASSWORD });
              resolve(otpRes);
            },
            onError: (error) => {
              if (error?.data?.error?.message === APIErrorCodes.Member.PhoneNotRegistered) {
                setError("phone", { message: t("number not registered. please try another") });
                return;
              }
              const rateLimitSeconds = getOtpCooldownSecondsFromRateLimitError(error);
              getCodeButtonRef.current?.startTimer(rateLimitSeconds ?? DEFAULT_OTP_COOLDOWN_SECONDS);
              const message = resolveAuthResponseError(error, t);
              if (message) {
                toast.error(message);
              }
              trackOTPRequestFailed(error, { source: OTPSource.FORGOT_PASSWORD });
              reject(error);
            },
          });
        });
      });
    });
  };

  const handleVerifyOTP = useCallback(
    async (code: string, phone: string) => {
      return new Promise((resolve, reject) => {
        verifyOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          code: code,
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastError: false,
            toastSuccess: false,
            onSuccess: (res) => {
              trackOTPVerified(OTPMethod.SMS, { source: OTPSource.FORGOT_PASSWORD });
              resolve(res);
            },
            onError: (error) => {
              if (error?.data?.error?.message) {
                setError("sms", { message: t(error?.data?.error.message) });
              }
              trackOTPFailedAttempt(error, { source: OTPSource.FORGOT_PASSWORD });
              reject(error?.data?.error?.message);
            },
          });
        });
      });
    },
    [handleApiResponse, t, verifyOTP, setError, phonePrefix, trackOTPVerified, trackOTPFailedAttempt],
  );

  const isConfirming = isVerifyingOTP || isSubmittingForgotPwd;

  return (
    <form
      id="form-forgot-secret-username-password"
      name="form-forgot-secret-username-password"
      onSubmit={handleFormSubmit((e) => delay().then(() => onSubmitForm(e)))}
      className={cn("flex flex-col gap-3", className)}
      autoComplete="off"
    >
      {/* PHONE NUMBER */}
      <TextField
        {...formRegister("phone")}
        error={!!formErrors.phone}
        helperText={t(formErrors.phone?.message ?? "", {
          minLength: phoneMinLength,
          maxLength: phoneMaxLength,
          startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
        })}
        placeholder={t("phone number")}
        type={"number"}
        inputMode={"numeric"}
        icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
        classes={{
          ...inputClasses,
          "input&": inputClasses["input&"] + (showPhoneInputPrefix ? " !ps-20" : ""),
          "prefixContainer&": showPhoneInputPrefix ? "absolute start-11 top-[10px] text-content-base" : "",
        }}
        size="sm"
        autoComplete="off"
        readOnly={phoneReadonly}
        prefix={showPhoneInputPrefix ? phonePrefix : undefined}
        fieldHelperTextManager={fieldHelperTextManager}
        data-element-id="input-forgot-secret-phone"
      />

      {/* OTP */}
      <div className="relative">
        <TextField
          {...formRegister("sms")}
          error={!!formErrors.sms}
          helperText={t(formErrors.sms?.message as string)}
          placeholder={t("enter code")}
          type={"text"}
          icon={<MdMail className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="one-time-code"
          inputMode="numeric"
          fieldHelperTextManager={fieldHelperTextManager}
          data-element-id="input-forgot-secret-code"
        />
        <GetCodeButton
          onGetCode={handleGetCode}
          className="absolute end-3 top-[9px]"
          classes={{
            "root&": "w-fit px-2",
          }}
          loading={isSendingOTP}
          ref={getCodeButtonRef}
        />
      </div>

      {/* PASSWORD */}
      <PasswordTextField
        {...formRegister("password")}
        error={!!formErrors.password}
        helperText={t(formErrors.password?.message as string)}
        placeholder={t("password")}
        classes={inputClasses}
        buttonClass="!top-[10px]"
        size="sm"
        autoComplete="new-password"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        fieldHelperTextManager={fieldHelperTextManager}
        data-element-id="input-forgot-secret"
      />

      <Button
        variant="primary"
        size="lg"
        type="submit"
        className="mt-2"
        disabled={isConfirming || isLoginLoading}
        loading={isConfirming || isLoginLoading}
        data-element-id="button-forgot-secret-submit"
      >
        {t("confirm")}
      </Button>
    </form>
  );
};

export default ForgotSecretUP;
